import { useMutation } from "@apollo/client";
import { useCallback } from "react";

import {
  type GetIssueQuery,
  type GetYourCasesQuery,
  type OpenNewIssueFromPreviewMutation,
  type OpenNewIssueFromPreviewMutationVariables,
  PreviewMessageType,
} from "@medbillai/generated-graphql-types";

import type { CaseType } from "@medbillai/schemas";

import { useAlerts } from "../provider/alerts";
import {
  getIssueQuery,
  getIssueQueryVariables,
} from "../screens/case/utils/queries";
import {
  getIssuesQueryV2,
  openNewIssueFromPreviewMutation,
} from "../screens/cases/utils/queries";
import { getYourCasesQuery } from "../screens/home/sections/queries";
import { showWelcomeScreenQuery } from "../screens/home/utils/queries";
import { analyticsService } from "./analytics";
import type { EventTypes } from "./analytics/types";
import { errorHandling } from "./apollo/utils";
import { useAuth } from "./auth";
import { SingularEvents, singularService } from "./singular";

type CreateIssueAnalyticsPayload = EventTypes["CreateIssue"];

type IssueRowForGetIssueCache = NonNullable<
  NonNullable<GetIssueQuery["issue__Issue"]>[number]
>;

export type UseCreateIssueParams = {
  caseType: CaseType;
  title: string;
  existingMessages: OpenNewIssueFromPreviewMutationVariables["input"]["messages"];
  analyticsProperties: CreateIssueAnalyticsPayload;
};

export type OpenIssueInput = { userMessage: string } | { documentId: string };

export function useCreateIssue({
  caseType,
  title,
  existingMessages,
  analyticsProperties,
}: UseCreateIssueParams) {
  const { user } = useAuth();
  const { showAlert } = useAlerts();

  const [openIssueFromPreview, { loading }] = useMutation(
    openNewIssueFromPreviewMutation,
    {
      context: errorHandling("caller"),
      update: (cache, { data: mutData }) => {
        const openNewIssue = mutData?.openNewIssueFromPreview;
        // Cache write is an optimization; if auth hasn't hydrated, skip.
        if (!openNewIssue || !user) {
          return;
        }
        // Write to cache with the real ID
        cache.writeQuery({
          query: getIssueQuery,
          variables: getIssueQueryVariables(openNewIssue.id, user.id),
          data: {
            __typename: "Query",
            // Same selection as `GetIssue`; cast bridges duplicate `gql` document
            // map entries that widen `OpenNewIssueFromPreview` vs `GetIssue` inference.
            issue__Issue: [openNewIssue as IssueRowForGetIssueCache],
            me: {
              __typename: "User_Type",
              id: user.id,
              webAgentLinks: [],
            },
          },
        });
        // Prepend the new case to the redesign home list so it's already there
        // when the user returns to home — no refetch, no pop-in. No-op if home
        // wasn't visited (empty cache); a cold home load then fetches it anyway.
        cache.updateQuery<GetYourCasesQuery>(
          { query: getYourCasesQuery },
          existing => {
            const issues = existing?.me?.issues;
            if (!existing?.me || !issues) {
              return existing;
            }
            if (issues.some(issue => issue.id === openNewIssue.id)) {
              return existing;
            }
            return {
              ...existing,
              me: {
                ...existing.me,
                issues: [
                  {
                    __typename: "issue__Issue_Type",
                    id: openNewIssue.id,
                    title: openNewIssue.title,
                    openedAt: openNewIssue.openedAt,
                    // A brand-new case has no last event or action-required work yet;
                    // real values arrive on the next fetch.
                    lastEvent: null,
                    actions: [],
                  },
                  ...issues,
                ],
              },
            };
          },
        );
      },
      refetchQueries: [
        {
          query: showWelcomeScreenQuery,
        },
        {
          query: getIssuesQueryV2,
        },
      ],
    },
  );

  const newIssue = useCallback(
    async (input: OpenIssueInput): Promise<OpenNewIssueFromPreviewMutation> => {
      if (!user) {
        throw new Error("Cannot create issue: user is not signed in");
      }
      analyticsService.trackEvent("CreateIssue", analyticsProperties);
      singularService.event(SingularEvents.createCase);

      const newMessages: OpenNewIssueFromPreviewMutationVariables["input"]["messages"] =
        [];
      if ("userMessage" in input) {
        newMessages.push({
          type: PreviewMessageType.USER,
          userMessageInfo: {
            message: input.userMessage,
          },
        });
      } else {
        newMessages.push({
          type: PreviewMessageType.USER,
          userMessageInfo: {
            documentId: input.documentId,
          },
        });
      }

      const { data: newData } = await openIssueFromPreview({
        variables: {
          input: {
            title,
            caseType,
            messages: [...existingMessages, ...newMessages],
            isFromUpload: false,
          },
          userId: user.id,
        },
      });
      if (!newData) {
        throw new Error("Failed to open new issue");
      }

      return newData;
    },
    [
      title,
      openIssueFromPreview,
      caseType,
      user,
      existingMessages,
      analyticsProperties,
    ],
  );

  const openIssue = useCallback(
    async (input: OpenIssueInput) => {
      try {
        const newData = await newIssue(input);
        const newId = newData?.openNewIssueFromPreview?.id;
        return newId;
      } catch (err) {
        console.error("Error creating issue", err);
        showAlert({
          type: "error",
          message: "Something went wrong. Please try again later.",
        });
      }
    },
    [newIssue, showAlert],
  );

  return {
    openIssue,
    loading,
  };
}
